import { useMemo } from "react";
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Hash, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCommands, type AppCommand } from "@/components/shell/commands";
import { PageBody, PageHeader, QueryBoundary } from "@/components/shell/page";
import { useProject, useWorkspace } from "@/components/shell/workspace-context";
import {
  TicketDetail,
  TicketDetailSkeleton,
  TicketNotFound,
  type TicketDetailScope,
} from "@/components/tickets/detail/ticket-detail";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/$workspaceSlug/$projectSlug/tickets/$number")({
  component: TicketPage,
});

/**
 * The number in a ticket's address, or null for anything else. Digits only,
 * with no leading zero, so a ticket has one address.
 */
function ticketNumber(raw: string): number | null {
  return /^[1-9]\d{0,14}$/.test(raw) ? Number(raw) : null;
}

function TicketPage() {
  const { t } = useTranslation("ticketDetail");
  const { t: tShell } = useTranslation("shell");
  const { workspaceSlug, projectSlug, number: raw } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const project = useProject();
  const number = ticketNumber(raw);

  const commands = useMemo<AppCommand[]>(
    () => [
      {
        id: "ticket.back",
        title: t("commands.back"),
        group: "navigation",
        keys: "escape",
        hidden: true,
        // Back to where the ticket was opened from, with its filters and
        // its place in the list. A ticket opened from a link has no such
        // place, and goes to the list.
        perform: () => {
          if (canGoBack) router.history.back();
          else {
            void navigate({
              to: "/$workspaceSlug/$projectSlug/tickets",
              params: { workspaceSlug, projectSlug },
            });
          }
        },
      },
    ],
    [t, navigate, router, canGoBack, workspaceSlug, projectSlug],
  );
  useCommands(commands);

  return (
    <>
      <PageHeader
        crumbs={[
          { label: project.name, to: "/$workspaceSlug/$projectSlug" },
          { label: tShell("nav.tickets"), to: "/$workspaceSlug/$projectSlug/tickets" },
        ]}
        title={number === null ? t("title") : `${project.key}-${number}`}
      />
      {number === null ? (
        <PageBody>
          <TicketNotFound />
        </PageBody>
      ) : (
        <QueryBoundary resetKey={`${project._id}:${number}`}>
          <NumberedTicket number={number} />
        </QueryBoundary>
      )}
    </>
  );
}

/** Finds the ticket behind the number, then shows it. */
function NumberedTicket({ number }: { number: number }) {
  const project = useProject();
  const { workspace } = useWorkspace();
  const ticketId = useQuery(api.tickets.byNumber, { projectId: project._id, number });
  const scope = useMemo<TicketDetailScope>(
    () => ({
      projectId: project._id,
      workspaceId: workspace._id,
      workspaceSlug: workspace.slug,
      projectSlug: project.slug,
    }),
    [project._id, project.slug, workspace._id, workspace.slug],
  );

  if (ticketId === undefined) return <TicketDetailSkeleton variant="page" />;
  if (ticketId === null) {
    return (
      <PageBody>
        <TicketNotFound />
      </PageBody>
    );
  }
  return (
    <>
      <CopyCommands identifier={`${project.key}-${number}`} />
      <TicketDetail ticketId={ticketId} scope={scope} variant="page" />
    </>
  );
}

/** "Copy link" and "Copy identifier", for a ticket that exists. */
function CopyCommands({ identifier }: { identifier: string }) {
  const { t } = useTranslation("ticketDetail");

  const commands = useMemo<AppCommand[]>(() => {
    const copy = async (text: string, copied: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast(copied);
      } catch {
        // No clipboard off https, or the browser said no.
        toast.error(t("commands.copyFailed"));
      }
    };
    return [
      {
        id: "ticket.copyLink",
        title: t("commands.copyLink"),
        group: "selection",
        icon: Link2,
        keywords: [identifier],
        // The address without the search: it opens the ticket and nothing else.
        perform: () =>
          void copy(
            `${window.location.origin}${window.location.pathname}`,
            t("commands.linkCopied"),
          ),
      },
      {
        id: "ticket.copyIdentifier",
        title: t("commands.copyIdentifier"),
        group: "selection",
        icon: Hash,
        keywords: [identifier],
        perform: () => void copy(identifier, t("commands.identifierCopied", { identifier })),
      },
    ];
  }, [t, identifier]);
  useCommands(commands);

  return null;
}
